import React, { FC, useContext, useCallback } from "react";
import Image from "next/image";
import styled from "styled-components";
import { ZipCodeContext } from "context/zipCode";
import { CartContext } from "context/cart";
import { UserContext } from "context/user";
import { InternalLink } from "@pepdirect/v3/ui/InternalLink";
import { BundleTileLayout } from "components/layout/BundleTileLayout";
import { AddToCartButtons } from "components/AddToCartButtons";
import { Price } from "components/Price";
import {
  logProductTileBuyButtonClickEvent,
  logProductClickedEvent,
} from "@pepdirect/v3/helpers/analyticsLogger";
import { getCloudflareLoader } from "@pepdirect/v3/helpers/cloudflareImages";
import { endpoints } from "config";
import { VariantFragment } from "services/graphql/generated";
import { Device } from "@pepdirect/v3/styles/screenSizes";
import st from "./bundleTile.module.scss";

interface BundleTileProps {
  item: VariantFragment;
  collection?: string;
  isInRecommendedSection?: boolean;
}

const LinkAndImageWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
`;

const ImageWrapper = styled.div`
  position: relative; /* Necessary for layout="fill" */
  width: 100%; /* Full width of the wrapper */
  height: 0; /* Set to 0 to allow padding-based aspect ratio */
  padding-top: 100%; /* 1:1 aspect ratio for a square */
`;

export const BundleTile: FC<BundleTileProps> = ({
  item,
  collection,
  isInRecommendedSection = false,
}) => {
  const { cart, updateCartItem } = useContext(CartContext);
  const { currentUserId } = useContext(UserContext);
  const { blockedItems } = useContext(ZipCodeContext);

  const {
    title,
    compareAtPrice,
    price,
    isOutOfStock,
    slug,
    inventoryQty,
    minQuantity,
    maximumPurchaseQuantity,
  } = item;

  const variantGtin = item.gtin;
  const imgUrl = item.imgUrls?.[0] || "";

  const isItemUnavailable = useCallback(
    (id: string) => {
      return blockedItems.includes(id);
    },
    [blockedItems]
  );

  const cartItem = cart?.variantItems?.find(
    (item) => item?.variant.gtin === variantGtin
  );
  const quantity = cartItem?.quantity || 0;
  const queryParams = new URLSearchParams();
  collection && queryParams.append("collection", collection);
  const internalUrl = `/product/${slug}?${queryParams.toString()}`;

  const handleProductClick = () => logProductClickedEvent(currentUserId, item);

  return (
    <BundleTileLayout isInRecommendedSection={isInRecommendedSection}>
      <LinkAndImageWrapper>
        <InternalLink href={internalUrl} onClick={handleProductClick}>
          <ImageWrapper>
            <Image
              className={st.image}
              src={imgUrl || ""}
              loader={getCloudflareLoader(endpoints.productsImgCdn)}
              alt={title || ""}
              fill
              sizes={`${Device.mobile} 100vw, ${Device.tablet} 50vw, ${Device.laptop} 25vw`}
              style={{
                objectFit: "contain",
              }}
            />
          </ImageWrapper>
        </InternalLink>
      </LinkAndImageWrapper>
      <InternalLink href={internalUrl} onClick={handleProductClick}>
        <h2 className={st.title}>{title}</h2>
      </InternalLink>
      {/* TODO: add library with truncation if needed */}
      <Price
        original={compareAtPrice}
        current={price}
        isInCents={!isInRecommendedSection}
      />
      <div className={st.add}>
        <AddToCartButtons
          quantity={quantity}
          onChange={(quantity: number) => {
            logProductTileBuyButtonClickEvent(currentUserId, item, quantity);
            return updateCartItem({
              variantGtin,
              quantity,
              subscriptionIntervalInDays:
                cartItem?.subscriptionIntervalInDays || 0,
            });
          }}
          outOfStock={!!isOutOfStock}
          unavailable={isItemUnavailable(variantGtin)}
          inventoryQty={inventoryQty || 0}
          minQuantity={minQuantity || 0}
          maximumPurchaseQuantity={maximumPurchaseQuantity}
        />
      </div>
    </BundleTileLayout>
  );
};
